import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Partner.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'Uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'Special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

const PartnerLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isInvite = params.get('invite') === 'true';

  const [mode, setMode] = useState(isInvite ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allPasswordRulesMet = PASSWORD_RULES.every((r) => r.test(password));

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!allPasswordRulesMet) {
      setError('Password does not meet all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/partner-auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Failed to create account');
        setLoading(false);
        return;
      }

      setSuccess('Account created successfully! You can now sign in.');
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to create account');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/partner-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Failed to sign in');
        setLoading(false);
        return;
      }

      setSuccess(`Welcome back, ${data.user.email}! Redirecting...`);
      setTimeout(() => navigate('/partner/dashboard', { state: { user: data.user } }), 1000);
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    }
    setLoading(false);
  };

  return (
    <>
      <section className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">
          {mode === 'signup' ? 'Create Your Partner Account' : 'Partner Login'}
        </h1>
      </section>

      <section className="ptn-content">
        <div className="ptn-login-container">
          {error && <div className="ptn-login-error">{error}</div>}
          {success && <div className="ptn-login-success">{success}</div>}

          {mode === 'signup' ? (
            <>
              <h2 className="ptn-heading">Set Up Your Account</h2>
              <p className="ptn-subheading">
                Create a password to access your partner portal.
              </p>
              <form className="ptn-login-form" onSubmit={handleSignup}>
                <label className="ptn-login-label" htmlFor="partner-email">Email</label>
                <input
                  className="ptn-login-input"
                  id="partner-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
                <label className="ptn-login-label" htmlFor="partner-password">Create Password</label>
                <input
                  className="ptn-login-input"
                  id="partner-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  required
                />
                {password.length > 0 && (
                  <ul className="ptn-password-rules">
                    {PASSWORD_RULES.map((rule) => {
                      const met = rule.test(password);
                      return (
                        <li key={rule.label} className={met ? 'ptn-rule-met' : 'ptn-rule-unmet'}>
                          <span className="ptn-rule-icon">{met ? '✓' : '✗'}</span> {rule.label}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <label className="ptn-login-label" htmlFor="partner-confirm-password">Confirm Password</label>
                <input
                  className="ptn-login-input"
                  id="partner-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  required
                />
                <button className="ptn-login-submit" type="submit" disabled={loading || !allPasswordRulesMet}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
              <p className="ptn-login-switch">
                Already have an account?{' '}
                <button className="ptn-login-switch-btn" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>
                  Sign In
                </button>
              </p>
            </>
          ) : (
            <>
              <h2 className="ptn-heading">Sign in to Your Partner Portal</h2>
              <p className="ptn-subheading">
                Access deal registration, training resources, collateral, and pipeline visibility.
              </p>
              <form className="ptn-login-form" onSubmit={handleLogin}>
                <label className="ptn-login-label" htmlFor="partner-login-email">Email</label>
                <input
                  className="ptn-login-input"
                  id="partner-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
                <label className="ptn-login-label" htmlFor="partner-login-password">Password</label>
                <input
                  className="ptn-login-input"
                  id="partner-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                />
                <button className="ptn-login-submit" type="submit" disabled={loading}>
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default PartnerLogin;
